import type { FC } from "react";
import { memo, useEffect, useRef } from "react";

import clsx from "clsx";

import type { IDiscountsPanel } from "@/src/discount/DiscountsPanel/DiscountsPanel";
import PageLayout from "@/src/shared/page-layout";
import type { IAjax } from "@Components/Hooks/useAjax";
import useAjax from "@Components/Hooks/useAjax";
import usePageInitialScroll from "@Components/Hooks/usePageInitialScroll";
import { Loading } from "@Components/Loading";
import { scrollToY } from "@Components/Modules/Helpers";
import type { IPanel } from "@Components/Panel";
import { Panel } from "@Components/Panel";
import { ShoppingMalls } from "@Routes/ShoppingMallLinks";

import type { ICategoriesCarouselItem } from "../common/CategoriesCarousel";
import { CategoriesCarousel } from "../common/CategoriesCarousel";
import type { IContentContainer } from "../common/ContentContainer";
import ContentContainer from "../common/ContentContainer";
import { getUrlParameterByName } from "../common/Helpers";
import DiscountsPanel from "../discount/DiscountsPanel";
import type { ILocationInfo } from "../location/LocationInfo";
import { LocationInfo } from "../location/LocationInfo";
import type { IShoppingMallsListsMap } from "./ShoppingMallsListsMap";
import ShoppingMallsListsMap from "./ShoppingMallsListsMap";

import * as cssPortal from "../shared/Portal.module.css";
import * as css from "./ShoppingMallsTab.module.css";

export interface IShoppingMallsTab extends IAjax<ICategoriesCarouselItem[]> {
    title: string;
    locationInfo: ILocationInfo;
    eventsTitle: string;
    events: IDiscountsPanel;
    bestPlaces: IPanel;
    listsMap: IShoppingMallsListsMap;
    positionParameter?: string;
    contentContainers?: IContentContainer;
}

const ShoppingMallsTab: FC<IShoppingMallsTab> = memo((props) => {
    const nearbyShoppingMallsRef = usePageInitialScroll<HTMLDivElement>(
        props.positionParameter,
        props.bestPlaces.clientId,
    );
    const waitAjaxCalls = useRef(1);
    const { data, isLoading, request, errorContainer } = useAjax(props);

    useEffect(request, []);

    const scrollToCategory = () => {
        if (waitAjaxCalls.current) {
            waitAjaxCalls.current -= 1;
            return true;
        }

        if (!props.positionParameter) {
            return true;
        }

        const categoryId = getUrlParameterByName(props.positionParameter);

        const element = document.getElementById(categoryId!);

        if (categoryId && element !== null) {
            scrollToY(element.offsetTop - 60);
        }

        return true;
    };

    const observedShoppingMalls = { ...props.listsMap.observedShoppingMalls };

    observedShoppingMalls.ajaxSuccess = [scrollToCategory];
    observedShoppingMalls.ajaxFail = [scrollToCategory];

    return (
        <PageLayout
            layoutAdUnitPath="galerie"
            slugTarget={ShoppingMalls}
            breadcrumbs={[{ labelKey: "shoppingMall" }]}
        >
            <div className={clsx(css.shoppingMallsTabComponent, cssPortal.standardContainer)}>
                <div className={css.locationContainer}>
                    <LocationInfo {...props.locationInfo} />
                </div>
                {props.events && (
                    <Panel className={css.eventsPanel} titleElement="h1" title={props.eventsTitle}>
                        <DiscountsPanel {...props.events} />
                    </Panel>
                )}
                {!isLoading && data?.length > 0 && (
                    <Panel {...props.bestPlaces} titleElement="h2" ref={nearbyShoppingMallsRef}>
                        {isLoading ? <Loading /> : <CategoriesCarousel categories={data} />}
                    </Panel>
                )}
                <ShoppingMallsListsMap
                    {...props.listsMap}
                    observedShoppingMalls={observedShoppingMalls}
                    isLazyLoading={!!data}
                />
                <ContentContainer {...props.contentContainers} />
                {errorContainer}
            </div>
        </PageLayout>
    );
});

export { ShoppingMallsTab };
